import { Link, useParams, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Card, Heading, PageShell } from "@/components/ui";
import { CourseEnrollPanel } from "@/components/course-enroll-panel";
import { CourseReviewsPanel } from "@/components/course-reviews-panel";
import { CourseWaitlistPanel } from "@/components/course-waitlist-panel";
import { useAuthStore } from "@/stores/authStore";
import { readPendingReferral, rememberReferral } from "@/lib/pending-referral";
import { fetchCourseDetail } from "@/services/api/tenants";

const PREVIEW_LIMIT = 12;

export function TenantCourseDetailPage() {
  const { tenantSlug = "", courseSlug = "" } = useParams<{
    tenantSlug: string;
    courseSlug: string;
  }>();
  const t = useTranslations("course");
  const tDetail = useTranslations("courseDetail");
  const user = useAuthStore((state) => state.user);
  const [searchParams] = useSearchParams();
  // PAY-9: a ?ref= link is remembered so it survives signing in or registering.
  const urlReferral = searchParams.get("ref")?.trim() || null;
  const referralCode = urlReferral ?? readPendingReferral(tenantSlug);
  useEffect(() => {
    if (urlReferral) rememberReferral(tenantSlug, urlReferral);
  }, [tenantSlug, urlReferral]);

  const detailQuery = useQuery({
    queryKey: ["course-detail", tenantSlug, courseSlug, referralCode],
    queryFn: () => fetchCourseDetail(tenantSlug, courseSlug, referralCode),
  });

  if (detailQuery.isLoading) {
    return (
      <PageShell data-testid="course-detail-loading">
        <p className="text-sm text-[var(--text-muted)]">{tDetail("loading")}</p>
      </PageShell>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <PageShell data-testid="course-detail-not-found">
        <Heading as="h1" level="page">
          {tDetail("notFound")}
        </Heading>
        <p className="mt-2 text-sm text-[var(--text-muted)]">{tDetail("notFoundHint")}</p>
        <Link
          to={`/t/${tenantSlug}/courses`}
          className="mt-4 inline-block text-sm text-[var(--text-heading)] underline"
        >
          {tDetail("backToCatalog")}
        </Link>
      </PageShell>
    );
  }

  const {
    course,
    prices,
    free,
    curriculum,
    sessionCount,
    reviewSummary,
    reviews,
    isWaitlistCourse,
    enrollmentState,
    canContinueLearning,
    canSelfEnroll,
    canSubmitReview,
    checkout,
    couponsAvailable,
    referral,
  } = detailQuery.data;

  const panelEnrollmentState = canContinueLearning
    ? "enrolled"
    : enrollmentState === "anonymous"
      ? "anonymous"
      : "not_enrolled";

  return (
    // ACC: one main landmark holds the hero and the body.
    <main data-testid="course-detail">
      <section className="border-b border-[var(--border)] bg-[var(--brand-purple)] text-white">
        <PageShell as="div" width="app" pad="none" className="py-10 sm:py-14">
          <Link
            to={`/t/${tenantSlug}/courses`}
            className="text-sm text-white/80 underline underline-offset-4 hover:text-white"
          >
            {t("backToCatalog")}
          </Link>
          <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                {course.courseType}
              </p>
              <h1 className="mt-2 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
                {course.title}
              </h1>
              {course.description && (
                <p className="mt-4 max-w-2xl leading-relaxed text-white/85">{course.description}</p>
              )}
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
                {sessionCount > 0 && <span>{t("sessionCount", { count: sessionCount })}</span>}
                {reviewSummary.count > 0 && (
                  <span>
                    ★ {reviewSummary.averageRating.toFixed(1)} ({reviewSummary.count})
                  </span>
                )}
              </div>
            </div>
            {course.thumbnailUrl ? (
              <img
                src={course.thumbnailUrl}
                alt=""
                className="aspect-video w-full rounded-xl object-cover shadow-lg"
              />
            ) : null}
          </div>
        </PageShell>
      </section>

      <PageShell
        as="div"
        width="app"
        className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start"
      >
        <div className="min-w-0 space-y-8 lg:order-1">
          {curriculum.length > 0 && (
            <section aria-labelledby="course-curriculum-heading" data-testid="course-curriculum">
              <Heading as="h2" level="section" id="course-curriculum-heading">
                {t("curriculum")}
              </Heading>
              <div className="mt-4 space-y-2">
                {curriculum.map((module, index) => (
                  <Card key={module.id} pad="none" tone="base">
                    <details
                      open={index === 0 && module.lessonCount <= PREVIEW_LIMIT}
                      className="group"
                    >
                      <summary className="flex cursor-pointer items-center justify-between gap-3 px-5 py-4 text-sm font-medium text-[var(--text-heading)]">
                        <span>{module.title}</span>
                        <span className="text-xs font-normal text-[var(--text-muted)]">
                          {t("moduleLessonCount", { count: module.lessonCount })}
                        </span>
                      </summary>
                      <ul className="border-t border-[var(--border)] px-5 py-3">
                        {module.lessons.slice(0, PREVIEW_LIMIT).map((lesson) => (
                          <li
                            key={lesson.id}
                            className="flex items-baseline justify-between gap-3 py-1.5 text-sm text-[var(--text-secondary)]"
                          >
                            <span>{lesson.title}</span>
                            {lesson.subtitle && (
                              <span className="shrink-0 text-xs text-[var(--text-muted)]">
                                {lesson.subtitle}
                              </span>
                            )}
                          </li>
                        ))}
                        {module.lessonCount > PREVIEW_LIMIT && (
                          <li className="py-1.5 text-sm text-[var(--text-muted)]">
                            {t("andMoreLessons", { count: module.lessonCount - PREVIEW_LIMIT })}
                          </li>
                        )}
                      </ul>
                    </details>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {!isWaitlistCourse && (
            <CourseReviewsPanel
              courseId={course.id}
              initialReviews={reviews}
              initialSummary={reviewSummary}
              canSubmitReview={canSubmitReview}
            />
          )}
        </div>

        <Card
          tone="brand"
          pad="lg"
          className="lg:order-2 lg:sticky lg:top-24"
          data-testid="course-pricing"
        >
          {isWaitlistCourse ? (
            <>
              <h2 className="text-lg font-medium">{t("comingSoon")}</h2>
              {enrollmentState === "enrolled" ? (
                <p
                  className="mt-6 text-sm text-[var(--text-secondary)]"
                  data-testid="course-waitlist-enrolled"
                >
                  {t("waitlistEnrolledPending")}
                </p>
              ) : enrollmentState === "anonymous" ? (
                <Link
                  to={`/t/${tenantSlug}/login?next=${encodeURIComponent(`/t/${tenantSlug}/courses/${courseSlug}`)}`}
                  className="mt-6 inline-block rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)]"
                  data-testid="course-detail-login"
                >
                  {t("signInToEnroll")}
                </Link>
              ) : canSelfEnroll && user ? (
                <CourseWaitlistPanel
                  courseId={course.id}
                  userEmail={user.email}
                  userName={user.name}
                />
              ) : null}
            </>
          ) : (
            <>
              <h2 className="text-lg font-medium">{t("pricing")}</h2>
              {free ? (
                <p className="mt-2 text-[var(--text-secondary)]" data-testid="course-price-free">
                  {t("free")}
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {prices.map((price) => (
                    <li
                      key={price.id}
                      className="flex items-baseline justify-between gap-4 text-[var(--text-primary)]"
                      data-testid={`course-price-${price.currency.toLowerCase()}`}
                    >
                      <span className="text-xs uppercase tracking-wide text-[var(--text-muted)]">
                        {price.currency}
                      </span>
                      <span className="text-xl font-semibold">{price.displayAmount}</span>
                    </li>
                  ))}
                </ul>
              )}
              <CourseEnrollPanel
                tenantSlug={tenantSlug}
                courseId={course.id}
                courseSlug={courseSlug}
                free={free}
                enrollmentState={panelEnrollmentState}
                canSelfEnroll={canSelfEnroll}
                prices={prices}
                checkout={checkout}
                couponsAvailable={couponsAvailable}
                referralCode={referral?.code ?? null}
                referralAccepted={referral?.accepted ?? false}
              />
            </>
          )}
        </Card>
      </PageShell>
    </main>
  );
}
